import { API_URL } from "../config";
import { ApiError, type Turn } from "./types";

const JSON_HEADERS = { "Content-Type": "application/json" };

async function parseTurn(res: Response): Promise<Turn> {
  if (!res.ok) {
    throw new ApiError(`Request failed (${res.status})`, res.status);
  }
  return (await res.json()) as Turn;
}

function toApiError(err: unknown): never {
  if (err instanceof ApiError) throw err;
  // fetch rejects (offline, DNS, CORS, abort) have no HTTP status.
  throw new ApiError(
    err instanceof Error ? err.message : "Network error",
    null,
  );
}

/** Start a new conversation session, optionally in a non-English language.
 *  `src` is the entry-source slug from a QR redirect (`qr-llr`) — analytics
 *  only, and the server drops it silently if malformed. */
export async function startConversation(
  signal?: AbortSignal,
  lang?: string,
  src?: string,
): Promise<Turn> {
  try {
    const params = new URLSearchParams();
    if (lang && lang !== "en") params.set("lang", lang);
    if (src) params.set("src", src);
    const qs = params.size > 0 ? `?${params.toString()}` : "";
    const res = await fetch(`${API_URL}/v1/conversations${qs}`, {
      method: "POST",
      headers: JSON_HEADERS,
      signal,
    });
    return await parseTurn(res);
  } catch (err) {
    toApiError(err);
  }
}

/**
 * Re-fetch the turn a session is waiting on, without advancing it.
 *
 * Used on resume after a refresh: the cached transcript deliberately carries no
 * `collected_data` (the structured PII payload must never be at rest in the
 * browser — shared-device privacy), so the answers are fetched back from the
 * server, where they already live. Without this the referral form would open
 * empty after a refresh.
 */
export async function getConversation(
  sessionId: string,
  signal?: AbortSignal,
): Promise<Turn> {
  try {
    const res = await fetch(
      `${API_URL}/v1/conversations/${encodeURIComponent(sessionId)}`,
      { signal },
    );
    return await parseTurn(res);
  } catch (err) {
    toApiError(err);
  }
}

/** Response of the GP-pathway identity gate (POST /api/v1/gp/session/verify). */
export interface GpVerifyResult {
  ok: boolean;
  identity_verified: boolean;
  late_entry: boolean;
  session_id: string;
  message?: string | null;
  preamble: string[];
}

/**
 * GP pathway: exchange the SMS-link token + date of birth for a started,
 * prefilled conversation session. Unlike the conversation endpoints this
 * surfaces the server's `detail` verbatim — every failure is deliberately the
 * same uniform message ("This link is invalid or has expired."), and the
 * screen shows exactly that.
 */
export async function verifyGpSession(
  token: string,
  dateOfBirth: string,
  signal?: AbortSignal,
): Promise<GpVerifyResult> {
  try {
    const res = await fetch(`${API_URL}/api/v1/gp/session/verify`, {
      method: "POST",
      headers: JSON_HEADERS,
      body: JSON.stringify({ token, date_of_birth: dateOfBirth }),
      signal,
    });
    if (!res.ok) {
      let detail: string | null = null;
      try {
        detail = ((await res.json()) as { detail?: string }).detail ?? null;
      } catch {
        /* non-JSON error body */
      }
      throw new ApiError(detail ?? `Request failed (${res.status})`, res.status);
    }
    return (await res.json()) as GpVerifyResult;
  } catch (err) {
    toApiError(err);
  }
}

/** Send the resident's message (or selected option value) for a session. */
export async function sendMessage(
  sessionId: string,
  message: string,
  signal?: AbortSignal,
): Promise<Turn> {
  try {
    const res = await fetch(
      `${API_URL}/v1/conversations/${encodeURIComponent(sessionId)}/messages`,
      { method: "POST", headers: JSON_HEADERS, body: JSON.stringify({ message }), signal },
    );
    return await parseTurn(res);
  } catch (err) {
    toApiError(err);
  }
}

/**
 * Submit the completed referral form. Persists to the dual store server-side.
 * Best-effort: the demo/conversation must still advance even if this fails, so
 * callers should not block the UX on it.
 */
export async function submitReferral(
  sessionId: string,
  form: Record<string, unknown>,
): Promise<{ referral_id: string | null; stored: boolean; reference: string | null }> {
  const res = await fetch(
    `${API_URL}/v1/conversations/${encodeURIComponent(sessionId)}/referral`,
    { method: "POST", headers: JSON_HEADERS, body: JSON.stringify({ form }) },
  );
  if (!res.ok) throw new Error(`referral submit failed: ${res.status}`);
  return (await res.json()) as {
    referral_id: string | null;
    stored: boolean;
    reference: string | null;
  };
}

/** Retry transient failures only — never 4xx (client errors / 404 stale session). */
export function isRetryable(err: unknown): boolean {
  if (err instanceof ApiError) return err.status === null || err.status >= 500;
  return true;
}

/** Languages this deployment can serve (English is always present). */
export async function fetchLanguages(signal?: AbortSignal): Promise<string[]> {
  try {
    const res = await fetch(`${API_URL}/v1/languages`, { headers: JSON_HEADERS, signal });
    if (!res.ok) return ["en"];
    const data = await res.json();
    return Array.isArray(data?.languages) ? data.languages : ["en"];
  } catch {
    return ["en"]; // never let a failed lookup break the conversation
  }
}

/**
 * Switch a live session's language. Forward-only: the server re-renders the
 * CURRENT question in the new language; earlier turns are not rewritten.
 */
export async function setLanguage(
  sessionId: string,
  lang: string,
  signal?: AbortSignal,
): Promise<Turn> {
  try {
    const res = await fetch(`${API_URL}/v1/conversations/${sessionId}/language`, {
      method: "POST",
      headers: JSON_HEADERS,
      body: JSON.stringify({ lang }),
      signal,
    });
    return await parseTurn(res);
  } catch (err) {
    toApiError(err);
  }
}
